import React, { useState, useEffect } from "react";
import {
  Settings,
  HardDrive,
  Download,
  AlertTriangle,
  Bot,
  Save,
  CheckCircle2,
  ShieldAlert,
  Sparkles,
  Image as ImageIcon,
  Upload,
  RotateCcw,
  Calculator,
  Percent,
  Receipt,
  Building,
  Calendar,
  RefreshCw,
  KeyRound,
  ShieldCheck,
  Copy,
  Mail,
  Lock,
} from "lucide-react";
import { api, API_BASE, getErrorMessage } from "../../../lib/api";
import { toast } from "sonner";

export default function SettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [testingGemini, setTestingGemini] = useState(false);
  const [geminiTestResult, setGeminiTestResult] = useState(null);

  const [form, setForm] = useState({
    app_name: "Rental Tracker",
    maintenance_mode: false,
    maintenance_message: "System undergoing scheduled maintenance. Please check back shortly.",
    gemini_api_key: "",
    gemini_model: "auto",
    gemini_api_key_configured: false,
    gemini_api_key_source: "none",
    irs_mileage_rate: "0.70",
    business_meals_deductible_pct: "50.0",
    bonus_depreciation_pct: "40.0",
    receipt_warning_threshold: "75.0",
    default_tax_year: "2025",
  });

  const [thumbnailInfo, setThumbnailInfo] = useState({ has_custom: false, url: "/thumbnail.png" });
  const [thumbKey, setThumbKey] = useState(Date.now());
  const [uploadingThumb, setUploadingThumb] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/settings");
      setForm((prev) => ({
        ...prev,
        ...res.data,
        gemini_api_key: "", // don't expose stored raw key
      }));
      if (res.data.thumbnail) {
        setThumbnailInfo(res.data.thumbnail);
      }
    } catch (err) {
      toast.error("Failed to load platform settings");
    } finally {
      setLoading(false);
    }
  };

  const loadThumbnail = async () => {
    try {
      const res = await api.get("/super-admin/branding/thumbnail");
      setThumbnailInfo(res.data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        app_name: form.app_name,
        maintenance_mode: form.maintenance_mode,
        maintenance_message: form.maintenance_message,
        gemini_model: form.gemini_model,
        irs_mileage_rate: form.irs_mileage_rate,
        business_meals_deductible_pct: form.business_meals_deductible_pct,
        bonus_depreciation_pct: form.bonus_depreciation_pct,
        receipt_warning_threshold: form.receipt_warning_threshold,
        default_tax_year: form.default_tax_year,
      };
      if (form.gemini_api_key.trim()) {
        payload.gemini_api_key = form.gemini_api_key.trim();
      }

      await api.patch("/super-admin/settings", payload);
      toast.success("Platform settings updated successfully");
      await loadSettings();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update platform settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleTestGemini = async () => {
    setTestingGemini(true);
    setGeminiTestResult(null);
    try {
      const payload = {};
      if (form.gemini_api_key.trim()) {
        payload.api_key = form.gemini_api_key.trim();
      }
      if (form.gemini_model) {
        payload.model = form.gemini_model;
      }
      const res = await api.post("/super-admin/gemini/test", payload);
      setGeminiTestResult({
        ok: true,
        message: res.data?.message || "Successfully connected to Google Gemini!",
        model: res.data?.model,
        latency_ms: res.data?.latency_ms,
      });
      toast.success("Gemini API connection verified!");
    } catch (err) {
      const msg = getErrorMessage(err, "Gemini API connection failed");
      setGeminiTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTestingGemini(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/super-admin/branding/thumbnail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Custom platform thumbnail uploaded");
      setThumbKey(Date.now());
      await loadThumbnail();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to upload thumbnail"));
    } finally {
      setUploadingThumb(false);
      e.target.value = "";
    }
  };

  const handleThumbnailReset = async () => {
    try {
      await api.delete("/super-admin/branding/thumbnail");
      toast.success("Reverted to official default app thumbnail");
      setThumbKey(Date.now());
      await loadThumbnail();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to reset thumbnail"));
    }
  };

  const handleDownloadBackup = () => {
    setDownloading(true);
    toast.info("Generating system snapshot archive. Download starting...");
    window.location.href = `${API_BASE}/super-admin/backup/export`;
    setTimeout(() => setDownloading(false), 3000);
  };

  return (
    <div className="space-y-8" data-testid="super-admin-settings-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Settings className="w-5 h-5 text-teal-400" />
            <span>Platform Configuration & Disaster Recovery</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Centralized settings for platform branding, global Gemini AI credentials, statutory IRS tax rules, and emergency controls.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1: Platform Branding & Social Share Thumbnail */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-teal-400" />
                <h3 className="text-sm font-bold text-white">Platform Branding & Social Preview</h3>
              </div>
              <span
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  thumbnailInfo.has_custom
                    ? "bg-teal-500/20 text-teal-400 border border-teal-500/30"
                    : "bg-slate-800 text-slate-400 border border-slate-700"
                }`}
              >
                {thumbnailInfo.has_custom ? "Custom Thumbnail" : "Official Default"}
              </span>
            </div>

            <p className="text-xs text-slate-400">
              The platform display title and Open Graph image embedded when this application is shared via iMessage, SMS text, WhatsApp, Slack, Facebook, or Twitter.
            </p>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                Global Platform Title
              </label>
              <input
                type="text"
                value={form.app_name}
                onChange={(e) => setForm({ ...form, app_name: e.target.value })}
                placeholder="e.g. Rental Tracker"
                data-testid="platform-title-input"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500 font-semibold"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Used in public browser titles, login page headers, and social link previews.
              </p>
            </div>

            {/* Live Social Card Preview */}
            <div className="max-w-sm rounded-xl overflow-hidden border border-slate-700/80 shadow-lg bg-slate-950">
              <div className="aspect-[1.91/1] w-full bg-slate-900 relative overflow-hidden flex items-center justify-center">
                <img
                  src={`${thumbnailInfo.url}?v=${thumbKey}`}
                  alt="App Social Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/thumbnail.png";
                  }}
                />
              </div>
              <div className="p-3 bg-slate-900/90 border-t border-slate-800">
                <div className="text-xs font-bold text-white truncate">{form.app_name || "Rental Tracker"}</div>
                <div className="text-[11px] text-slate-400 truncate">{window.location.host || "bkcproperties.synology.me"}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <label className="px-3.5 py-2 rounded-xl bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/30 text-teal-300 font-bold text-xs cursor-pointer flex items-center gap-1.5 transition-colors">
                <Upload size={13} />
                <span>{uploadingThumb ? "Uploading..." : "Upload Custom Thumbnail"}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleThumbnailUpload}
                  disabled={uploadingThumb}
                />
              </label>

              {thumbnailInfo.has_custom && (
                <button
                  type="button"
                  onClick={handleThumbnailReset}
                  className="px-3.5 py-2 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-400 hover:text-white font-medium text-xs flex items-center gap-1.5 transition-colors"
                >
                  <RotateCcw size={13} />
                  <span>Reset to Default</span>
                </button>
              )}
            </div>
          </div>

          {/* Card 2: Federal IRS Statutory Rates & Tax Defaults */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-5">
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-teal-400" />
              <h3 className="text-sm font-bold text-white">Federal IRS Statutory Rates & Defaults</h3>
            </div>

            <p className="text-xs text-slate-400">
              Statutory federal tax parameters automatically applied to Mileage Logs, Capital Asset Depreciation, Meals deductions, and Schedule E tax packages across all companies.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  IRS Standard Mileage Rate ($ / mi)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-slate-400 font-bold">$</span>
                  <input
                    type="number"
                    step="0.005"
                    value={form.irs_mileage_rate}
                    onChange={(e) => setForm({ ...form, irs_mileage_rate: e.target.value })}
                    data-testid="irs-mileage-rate-input"
                    className="w-full pl-7 pr-3 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500 font-mono font-bold"
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">2025 Standard: $0.70/mi</p>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Business Meals Deductible (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="1"
                    value={form.business_meals_deductible_pct}
                    onChange={(e) => setForm({ ...form, business_meals_deductible_pct: e.target.value })}
                    data-testid="business-meals-pct-input"
                    className="w-full pl-3 pr-7 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500 font-mono font-bold"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-bold">%</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">IRS IRC § 274(n): 50%</p>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Bonus Depreciation Rate (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="5"
                    value={form.bonus_depreciation_pct}
                    onChange={(e) => setForm({ ...form, bonus_depreciation_pct: e.target.value })}
                    data-testid="bonus-depreciation-pct-input"
                    className="w-full pl-3 pr-7 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500 font-mono font-bold"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-bold">%</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">2025 Phase-out: 40% (TCJA)</p>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  IRS § 274(d) Receipt Threshold ($)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-slate-400 font-bold">$</span>
                  <input
                    type="number"
                    step="1"
                    value={form.receipt_warning_threshold}
                    onChange={(e) => setForm({ ...form, receipt_warning_threshold: e.target.value })}
                    data-testid="receipt-threshold-input"
                    className="w-full pl-7 pr-3 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500 font-mono font-bold"
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Warns if receipt missing &gt; $75</p>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                Default Reporting Tax Year
              </label>
              <input
                type="text"
                value={form.default_tax_year}
                onChange={(e) => setForm({ ...form, default_tax_year: e.target.value })}
                placeholder="2025"
                data-testid="default-tax-year-input"
                className="w-full px-3.5 py-2 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500 font-mono font-bold max-w-[160px]"
              />
            </div>
          </div>

          {/* Card 3: Centralized Gemini AI Engine */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal-400" />
                <h3 className="text-sm font-bold text-white">Centralized Gemini AI Engine</h3>
              </div>

              {form.gemini_api_key_configured ? (
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Active ({form.gemini_api_key_source})
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-slate-800 text-slate-400 border border-slate-700">
                  Not Configured
                </span>
              )}
            </div>

            <p className="text-xs text-slate-400">
              Powers the AI Receipt Scanner, itemized deduction categorization, and mortgage statement parsing across all tenant companies automatically.
            </p>

            <div className="space-y-3.5">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Gemini API Key
                </label>
                <input
                  type="password"
                  value={form.gemini_api_key}
                  onChange={(e) => setForm({ ...form, gemini_api_key: e.target.value })}
                  placeholder={
                    form.gemini_api_key_configured
                      ? "•••••••• (Key saved — enter new value to update)"
                      : "Enter Google AI Studio API Key (AIza...)"
                  }
                  data-testid="gemini-key-input"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Default Gemini Vision Model
                </label>
                <select
                  value={form.gemini_model}
                  onChange={(e) => setForm({ ...form, gemini_model: e.target.value })}
                  data-testid="gemini-model-select"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value="auto">Auto (Default Recommended)</option>
                  <option value="gemini-2.0-flash">gemini-2.0-flash (Latest Fast Model)</option>
                  <option value="gemini-1.5-flash">gemini-1.5-flash (Reliable Default)</option>
                  <option value="gemini-1.5-pro">gemini-1.5-pro (High Reasoning)</option>
                </select>
              </div>

              {/* Test Connection Button & Live Status */}
              <div className="pt-2 border-t border-slate-800/80 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-400">
                    Verify credentials and API latency:
                  </span>
                  <button
                    type="button"
                    onClick={handleTestGemini}
                    disabled={testingGemini || (!form.gemini_api_key_configured && !form.gemini_api_key.trim())}
                    className="px-3.5 py-1.5 rounded-xl bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/30 text-teal-300 font-bold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${testingGemini ? "animate-spin text-teal-400" : ""}`} />
                    <span>{testingGemini ? "Testing..." : "Test Connection"}</span>
                  </button>
                </div>

                {geminiTestResult && (
                  <div
                    className={`p-3 rounded-xl border text-xs flex items-start gap-2.5 ${
                      geminiTestResult.ok
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                        : "bg-rose-500/10 border-rose-500/30 text-rose-300"
                    }`}
                  >
                    {geminiTestResult.ok ? (
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
                    )}
                    <div className="space-y-0.5">
                      <div className="font-semibold text-white">
                        {geminiTestResult.ok ? "Connection Verified" : "Connection Failed"}
                      </div>
                      <div className="text-[11px] leading-relaxed text-slate-300">
                        {geminiTestResult.message}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Card 4: Emergency Maintenance Mode */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-bold text-white">Emergency Maintenance Mode</h3>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    form.maintenance_mode
                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/40 animate-pulse"
                      : "bg-slate-800 text-slate-400 border border-slate-700"
                  }`}
                >
                  {form.maintenance_mode ? "ACTIVE (503 LOCKOUT)" : "STANDBY"}
                </span>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.maintenance_mode}
                    onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              When active, all tenant company requests return HTTP 503 Maintenance with the custom announcement below. Super Admin routes remain fully functional.
            </p>

            {form.maintenance_mode ? (
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-2">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-300">
                  Tenant Broadcast Notice
                </label>
                <textarea
                  rows={2}
                  value={form.maintenance_message}
                  onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })}
                  placeholder="System undergoing scheduled maintenance. Please check back shortly."
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-amber-500/30 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 text-[11px] text-slate-500 flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>Normal operations: all tenant domains, APIs, and portals are online and operational.</span>
              </div>
            )}
          </div>

          {/* Card 5: Disaster Recovery 1-Click Backup Export */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col justify-between lg:col-span-2">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-bold text-white">1-Click Full System Backup</h3>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Export an uncompressed / gzipped system archive (<code className="text-teal-300">.tar.gz</code>) containing all database files and uploaded receipts across the entire platform.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 space-y-2 text-xs">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Included in snapshot archive:
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-slate-400 text-[11px]">
                    <li><code className="text-slate-200">accounts_registry.db</code> (Registry, super admins, global settings)</li>
                    <li><code className="text-slate-200">lapen.db</code> (Default primary account database)</li>
                    <li><code className="text-slate-200">accounts/*/*.db</code> (All isolated customer company databases)</li>
                    <li><code className="text-slate-200">accounts/*/uploads</code> (All tenant uploaded receipts & documents)</li>
                  </ul>
                </div>

                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex flex-col justify-between">
                  <div className="flex items-start gap-2.5">
                    <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                    <p className="text-[11px] leading-relaxed">
                      Keep the generated archive in a secure, off-site location. It contains relational financial and personal data for all tenants.
                    </p>
                  </div>

                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={handleDownloadBackup}
                      disabled={downloading}
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs tracking-wide shadow-xl shadow-amber-500/20 flex items-center justify-center gap-2 transition-all active:scale-[0.99] disabled:opacity-50"
                    >
                      <Download className="w-4 h-4" />
                      <span>{downloading ? "Preparing Backup..." : "Download Full System Backup (.tar.gz)"}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global Save Button Bar */}
        <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-900 border border-slate-800 shadow-xl">
          <div className="text-xs text-slate-400">
            Changes to platform title, IRS rates, and Gemini AI apply immediately across all tenant companies.
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-all-platform-settings"
            className="px-6 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-black text-xs tracking-wide shadow-lg shadow-teal-500/20 flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? "Saving Changes..." : "Save Platform Settings"}</span>
          </button>
        </div>
      </form>

      {/* Super Admin Security & 2-Factor Recovery Kit Management */}
      <SuperAdminSecurityCard />
    </div>
  );
}

function SuperAdminSecurityCard() {
  const [secInfo, setSecInfo] = useState(null);
  const [loadingSec, setLoadingSec] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);

  const [rotatePassword, setRotatePassword] = useState("");
  const [rotatingKit, setRotatingKit] = useState(false);
  const [generatedKit, setGeneratedKit] = useState(null);

  const loadSecurityInfo = async () => {
    setLoadingSec(true);
    try {
      const res = await api.get("/super-admin/security");
      setSecInfo(res.data);
      setEmail(res.data?.email || "");
    } catch (err) {
      // ignore
    } finally {
      setLoadingSec(false);
    }
  };

  useEffect(() => {
    loadSecurityInfo();
  }, []);

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Please enter your current Master Password");
      return;
    }
    setSavingCreds(true);
    try {
      await api.post("/super-admin/security/credentials", {
        current_password: currentPassword,
        email: email.trim(),
        new_password: newPassword ? newPassword : null,
      });
      toast.success("Super Admin credentials & recovery email updated");
      setCurrentPassword("");
      setNewPassword("");
      await loadSecurityInfo();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update Super Admin credentials"));
    } finally {
      setSavingCreds(false);
    }
  };

  const handleRotateRecoveryKit = async (e) => {
    e.preventDefault();
    if (!rotatePassword) {
      toast.error("Enter your current Master Password to generate a new Recovery Kit");
      return;
    }
    setRotatingKit(true);
    try {
      const res = await api.post("/super-admin/security/recovery-kit", {
        current_password: rotatePassword,
      });
      setGeneratedKit(res.data?.recovery_kit);
      setRotatePassword("");
      toast.success("New Emergency Recovery Kit generated! Save it immediately.");
      await loadSecurityInfo();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to generate Recovery Kit"));
    } finally {
      setRotatingKit(false);
    }
  };

  const formatRecoveryKitText = () => {
    if (!generatedKit) return "";
    return [
      "============================================================",
      "  RENTAL TRACKER — SUPER ADMIN EMERGENCY RECOVERY KIT",
      "============================================================",
      `Username: ${secInfo?.username || "root"}`,
      `Email:    ${secInfo?.email || "(not set)"}`,
      `Created:  ${new Date().toISOString()}`,
      "",
      "1. MASTER RECOVERY KEY (Reusable Factor 2 for Password Reset):",
      `   ${generatedKit.master_recovery_key}`,
      "",
      "2. SINGLE-USE BACKUP CODES (8):",
      ...(generatedKit.backup_codes || []).map((c, idx) => `   ${idx + 1}. ${c}`),
      "",
      "Keep this file in a secure password manager or offline vault.",
      "============================================================",
    ].join("\n");
  };

  const handleCopyKit = () => {
    navigator.clipboard.writeText(formatRecoveryKitText());
    toast.success("Recovery Kit copied to clipboard");
  };

  const handleDownloadKit = () => {
    const blob = new Blob([formatRecoveryKitText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rental-tracker-superadmin-recovery-kit.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Downloaded Recovery Kit (.txt)");
  };

  if (loadingSec) return null;

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-teal-400">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">
              Super Admin Security & 2-Factor Recovery Kit
            </h3>
            <p className="text-xs text-slate-400">
              Manage your recovery email, Master Password, and offline Recovery Key / Backup Codes required for "Forgot Password?".
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
              secInfo?.has_recovery_key
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : "bg-amber-500/15 text-amber-300 border-amber-500/30"
            }`}
          >
            {secInfo?.has_recovery_key
              ? `Recovery Key Active (${secInfo.unused_backup_codes}/${secInfo.total_backup_codes} Backup Codes)`
              : "Recovery Kit Not Generated Yet"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Recovery Email & Master Password Update */}
        <form onSubmit={handleUpdateCredentials} className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-teal-400">
            <Lock className="w-3.5 h-3.5" />
            <span>Recovery Email & Password</span>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
              Recovery Email (Receives 6-Digit OTP)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@yourdomain.com"
              className="w-full px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-700/80 text-xs text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                Current Master Password *
              </label>
              <input
                type="password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Required to save"
                className="w-full px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-700/80 text-xs text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                New Password (Optional)
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Leave blank to keep"
                className="w-full px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-700/80 text-xs text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={savingCreds}
            className="px-4 py-2 rounded-xl bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-300 font-bold text-xs transition-colors disabled:opacity-50"
          >
            {savingCreds ? "Updating..." : "Update Email / Password"}
          </button>
        </form>

        {/* Right: Generate / Rotate Master Recovery Key & Backup Codes */}
        <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3.5 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-400">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Offline Recovery Kit (Factor 2)</span>
              </div>
              {secInfo?.recovery_key_hint && (
                <code className="text-[11px] text-slate-300 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                  {secInfo.recovery_key_hint}
                </code>
              )}
            </div>

            <p className="text-xs text-slate-400 leading-relaxed">
              Generating a new Recovery Kit issues a fresh <strong>Master Recovery Key</strong> and <strong>8 Single-Use Backup Codes</strong>, immediately invalidating any previous recovery codes.
            </p>
          </div>

          <form onSubmit={handleRotateRecoveryKit} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2.5 pt-2">
            <div className="flex-1">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                Confirm Master Password
              </label>
              <input
                type="password"
                required
                value={rotatePassword}
                onChange={(e) => setRotatePassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-700/80 text-xs text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <button
              type="submit"
              disabled={rotatingKit}
              className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs whitespace-nowrap transition-colors disabled:opacity-50"
            >
              {rotatingKit
                ? "Generating..."
                : secInfo?.has_recovery_key
                ? "Rotate Recovery Kit"
                : "Generate Recovery Kit"}
            </button>
          </form>
        </div>
      </div>

      {/* Newly Generated Recovery Kit Banner (Shown Once) */}
      {generatedKit && (
        <div className="p-5 rounded-2xl bg-slate-950 border border-amber-500/40 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="text-sm font-bold text-amber-300">
                Save Your New Emergency Recovery Kit Now
              </h4>
              <p className="text-xs text-slate-400">
                These plain-text codes are shown only once. Store them in your password manager.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyKit}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5 text-teal-400" />
                <span>Copy All</span>
              </button>
              <button
                type="button"
                onClick={handleDownloadKit}
                className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-xs font-bold text-slate-950 flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download .TXT</span>
              </button>
            </div>
          </div>

          <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-1">
              Master Recovery Key (Reusable)
            </div>
            <div className="font-mono text-sm font-bold text-white select-all">
              {generatedKit.master_recovery_key}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
              8 Single-Use Backup Codes
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(generatedKit.backup_codes || []).map((code) => (
                <div
                  key={code}
                  className="font-mono text-xs text-teal-300 bg-slate-900 border border-slate-800 rounded-lg py-1.5 px-2 text-center select-all"
                >
                  {code}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
